import { msg } from "@lingui/core/macro";
import { Dialog, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FolderPlus, ListPlus } from "@fleet/icons";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/providers/i18n-provider";

interface ChoiceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreateList: (groupId?: string) => void;
  onCreateGroup: () => void;
  groupId?: string;
}

export function ChoiceDialog(props: ChoiceDialogProps) {
  const { t } = useI18n();
  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange} contentClass="max-w-md p-0">
      <DialogHeader>
        <DialogTitle class="text-sm font-bold uppercase tracking-widest opacity-50 pl-2">
          {t(msg`Create new`)}
        </DialogTitle>
      </DialogHeader>

      <div class="p-2 space-y-1">
        <button
          type="button"
          onClick={() => {
            // Navigates to target's own URL (?dialog=list-new). We do NOT close
            // manually: navigation replaces search-param and the URL→signal
            // effect closes the choice automatically. Closing here would overwrite navigation
            // (replace) and return to base URL instead of directing to destination.
            props.onCreateList(props.groupId);
          }}
          class="w-full flex items-center gap-3 p-4 rounded-2xl hover:bg-accent transition-colors text-left group cursor-pointer"
        >
          <div class="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center text-primary group-hover:scale-110 transition-transform">
            <ListPlus class="h-5 w-5" />
          </div>
          <div>
            <div class="font-bold">{t(msg`New List`)}</div>
            <div class="text-xs text-muted-foreground">{t(msg`Organize your tasks by topic`)}</div>
          </div>
        </button>

        <button
          type="button"
          onClick={() => {
            // Navigates to its own URL (?dialog=group-new); the URL→signal effect
            // closes the choice. (See note on previous option.)
            props.onCreateGroup();
          }}
          class="w-full flex items-center gap-3 p-4 rounded-2xl hover:bg-accent transition-colors text-left group cursor-pointer"
        >
          <div class="h-10 w-10 rounded-full bg-muted flex items-center justify-center text-muted-foreground group-hover:scale-110 transition-transform">
            <FolderPlus class="h-5 w-5" />
          </div>
          <div>
            <div class="font-bold">{t(msg`New Group`)}</div>
            <div class="text-xs text-muted-foreground">{t(msg`Group your related lists`)}</div>
          </div>
        </button>
      </div>

      <div class="p-4 bg-muted/20">
        <Button
          variant="ghost"
          class="w-full h-10 font-bold rounded-xl"
          onClick={() => {
            props.onOpenChange(false);
          }}
        >
          {t(msg`Cancel`)}
        </Button>
      </div>
    </Dialog>
  );
}
